import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { BiTrash } from 'react-icons/bi';
import { useAssetActions } from '@/components';
import type { IStationSchema } from '@/components/station/schema';
import { AssetEditor } from '@/core/molecules';
import { Button } from '@/core/ui';
import type { Picture } from '@/graphql/gql/graphql';
import { getPictureSrc } from '@/utils/picture';

export const StationCoverField = () => {
  const { setValue, watch } = useFormContext<IStationSchema>();
  const [uploadOpen, setUploadOpen] = useState(false);
  const { createAsset, createAssetsLoading } = useAssetActions();
  const picture = watch('picture');
  const picturePreviewUrl = picture ? getPictureSrc(picture as Picture) : '';

  const handleUploadSave = useCallback(
    async (data: { file: File; name: string; sensitive: boolean; caption?: string | null }) => {
      const res = await createAsset([
        {
          file: data.file,
          name: data.name,
          sensitive: data.sensitive,
          caption: data.caption,
        },
      ]);
      const pic = res.data?.admin?.createAssets?.[0];
      if (pic?.id) {
        setValue('picture', pic as IStationSchema['picture'], {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      setUploadOpen(false);
    },
    [createAsset, setValue],
  );

  const clearCover = () => {
    setValue('picture', null, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <div className='flex w-full min-w-0 flex-col gap-2 bg-default-100 rounded-md p-2 border border-default-200'>
      <span className='text-sm text-default-600'>
        Cover image <span className='text-danger'>*</span>
      </span>
      <div className='relative w-full max-h-48 min-h-[140px] aspect-video rounded-md overflow-hidden bg-default-100 border border-default-200'>
        {picturePreviewUrl ? (
          <img src={picturePreviewUrl} alt='' className='size-full object-cover' />
        ) : (
          <div className='size-full flex items-center justify-center text-xs text-default-400 px-3 text-center'>
            No image
          </div>
        )}
      </div>
      <div className='flex w-full flex-row gap-2 mt-2'>
        <Button fullWidth size='sm' variant='flat' onPress={() => setUploadOpen(true)}>
          Upload asset
        </Button>
        {(picture || picturePreviewUrl) && (
          <Button
            fullWidth
            size='sm'
            variant='flat'
            color='warning'
            startContent={<BiTrash className='size-4' />}
            onPress={clearCover}
          >
            Remove
          </Button>
        )}
      </div>
      <AssetEditor
        isOpen={uploadOpen}
        title='Upload cover image'
        loading={createAssetsLoading}
        onClose={() => setUploadOpen(false)}
        onSave={handleUploadSave}
      />
    </div>
  );
};
